import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, Droplets, Shield, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import HeroSection from '@/components/sections/HeroSection';
import TestimonialsSection from '@/components/sections/TestimonialsSection';
import GallerySection from '@/components/sections/GallerySection';
import SisterServiceBanner from '@/components/SisterServiceBanner';
import SectionDivider from '@/components/SectionDivider';
import { Button } from '@/components/ui/button';
import { faqs } from '@/components/sections/FaqSection';

// Map deprecated /#anchor URLs to their new dedicated routes.
const HASH_REDIRECTS: Record<string, string> = {
  '#hero': '/',
  '#about': '/about',
  '#services': '/services',
  '#process': '/process',
  '#faq': '/faq',
  '#areas': '/areas-served',
  '#contact': '/contact',
};

const teaserServices = [
  {
    slug: 'automotive-restoration',
    icon: Zap,
    title: 'Automotive Restoration',
    cta: 'Explore automotive restoration',
    blurb: 'Engine parts, alloy wheels and classic restorations restored to factory finish.',
  },
  {
    slug: 'industrial-cleaning',
    icon: Droplets,
    title: 'Industrial Cleaning',
    cta: 'Read about industrial cleaning',
    blurb: 'Rust, paint and scale removed from tooling and equipment — no dust, no damage.',
  },
  {
    slug: 'marine-cleaning',
    icon: Shield,
    title: 'Marine Cleaning',
    cta: 'See marine cleaning services',
    blurb: 'Salt corrosion stripped from props, outboards and hardware without pitting.',
  },
];

const Index = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect legacy single-page anchors (e.g. /#services) to the new routes.
  useEffect(() => {
    if (location.hash && HASH_REDIRECTS[location.hash]) {
      navigate(HASH_REDIRECTS[location.hash], { replace: true });
    }
  }, [location.hash, navigate]);

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'AutomotiveBusiness'],
    '@id': 'https://precisionvapour.co.nz/#business',
    name: 'Precision Vapour Blasting',
    alternateName: [
      'Precision Vapour Blasting Christchurch',
      'Precision Vapor Blasting',
      'Vapor Blasting Christchurch',
      'Vapor Honing Christchurch',
    ],
    keywords:
      'vapour blasting, vapor blasting, vapour blasting Christchurch, vapor blasting Christchurch, vapor honing, wet blasting, aqua blasting',
    description:
      'Professional vapour blasting (vapor blasting) services for automotive, industrial and marine applications in Christchurch, New Zealand.',
    image: 'https://precisionvapour.co.nz/lovable-uploads/515b84fc-0026-48a0-964d-e7074f1cf660.png',
    logo: 'https://precisionvapour.co.nz/lovable-uploads/515b84fc-0026-48a0-964d-e7074f1cf660.png',
    telephone: '+64226578662',
    email: 'info@precisionvapour.co.nz',
    url: 'https://precisionvapour.co.nz',
    currenciesAccepted: 'NZD',
    paymentAccepted: 'Cash, Bank Transfer, Credit Card',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Unit 2, 1 Doric Way, Islington',
      addressLocality: 'Christchurch',
      addressRegion: 'Canterbury',
      postalCode: '8042',
      addressCountry: 'NZ',
    },
    geo: { '@type': 'GeoCoordinates', latitude: '-43.538567', longitude: '172.520087' },
    areaServed: [
      { '@type': 'City', name: 'Christchurch' },
      { '@type': 'AdministrativeArea', name: 'Canterbury' },
    ],
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
    sameAs: ['https://www.facebook.com/profile.php?id=61574602346048'],
    priceRange: '$$',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      bestRating: '5',
      worstRating: '1',
      ratingCount: '3',
      reviewCount: '3',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Precision Vapour Blasting',
    url: 'https://precisionvapour.co.nz/',
  };

  // Keep a small FAQ schema on the homepage too — Google still rewards it
  // and the FAQ teaser links out to the full /faq page.
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.slice(0, 4).map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="flex flex-col min-h-screen overflow-hidden">
      <Helmet>
        <title>Vapour Blasting Christchurch | Wet Blasting Specialists NZ</title>
        <meta
          name="description"
          content="Vapour blasting in Christchurch & Canterbury. Eco-friendly wet/aqua blasting for engines, alloy wheels, marine and industrial parts. Free quotes."
        />
        <link rel="canonical" href="https://precisionvapour.co.nz/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://precisionvapour.co.nz/" />
        <meta property="og:title" content="Vapour Blasting Christchurch | Precision Vapour Blasting" />
        <meta
          property="og:description"
          content="Eco-friendly wet/aqua blasting for engines, alloy wheels, marine & industrial parts. Free quotes."
        />
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Navbar />
      <main>
        <HeroSection />
        <SectionDivider />

        <GallerySection />
        <SectionDivider />

        {/* Services teaser */}
        <section className="section-padding bg-background">
          <div className="container-wide mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <p className="text-primary font-medium tracking-[0.3em] uppercase mb-4 text-sm">
                What We Do
              </p>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Our <span className="gradient-text">Services</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Specialised vapour blasting for automotive, industrial and marine applications
                across Canterbury. Known as{' '}
                <Link to="/vapor-blasting" className="text-primary hover:underline">
                  vapor blasting or vapor honing
                </Link>{' '}
                in the US — same process, same results.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mb-10">
              {teaserServices.map(({ slug, icon: Icon, title, blurb, cta }) => (
                <Link
                  key={slug}
                  to={`/services/${slug}`}
                  className="glass-card rounded-xl p-8 hover:border-primary/50 transition-colors group flex flex-col"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
                    <Icon className="text-primary w-7 h-7" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-2xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors">
                    {title}
                  </h3>
                  <p className="text-muted-foreground mb-6 flex-1">{blurb}</p>
                  <span className="inline-flex items-center gap-2 text-primary font-medium">
                    {cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
            <div className="text-center">
              <Button variant="heroOutline" asChild>
                <Link to="/services">See all services</Link>
              </Button>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* About teaser */}
        <section className="section-padding bg-background">
          <div className="container-wide mx-auto max-w-4xl text-center">
            <p className="text-primary font-medium tracking-[0.3em] uppercase mb-4 text-sm">
              About Us
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Surface restoration <span className="gradient-text">done right</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Based in Islington, Christchurch, we deliver vapour blasting results that traditional
              methods can't match — gentle on delicate alloy, brass and stainless components, with
              no warping, pitting or surface damage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="hero" asChild>
                <Link to="/about">More about us</Link>
              </Button>
              <Button variant="heroOutline" asChild>
                <Link to="/process">How the process works</Link>
              </Button>
            </div>
          </div>
        </section>

        <SectionDivider />
        <TestimonialsSection />

        <SectionDivider />


        {/* FAQ + Areas teaser */}
        <section className="section-padding bg-background">
          <div className="container-wide mx-auto max-w-4xl grid md:grid-cols-2 gap-8 text-center">
            <div className="glass-card rounded-xl p-8">
              <h3 className="font-display text-2xl font-bold mb-4 text-foreground">
                Got questions?
              </h3>
              <p className="text-muted-foreground mb-6">
                Cost, turnaround, aftercare, what we can clean — answers to the most common
                questions about vapour blasting.
              </p>
              <Button variant="heroOutline" asChild>
                <Link to="/faq">Read the FAQ</Link>
              </Button>
            </div>
            <div className="glass-card rounded-xl p-8">
              <h3 className="font-display text-2xl font-bold mb-4 text-foreground">
                Service area
              </h3>
              <p className="text-muted-foreground mb-6">
                Christchurch, Hornby, Rolleston, Rangiora, Ashburton, Timaru and beyond. Ship parts
                from anywhere in New Zealand.
              </p>
              <Button variant="heroOutline" asChild>
                <Link to="/areas-served">See areas served</Link>
              </Button>
            </div>
          </div>
        </section>

        <SectionDivider />
        <div className="py-8 md:py-12 bg-background">
          <SisterServiceBanner />
        </div>
        <SectionDivider />

        {/* Contact CTA */}
        <section className="section-padding bg-background">
          <div className="container-wide mx-auto max-w-3xl text-center">
            <p className="text-primary font-medium tracking-[0.3em] uppercase mb-4 text-sm">
              Contact
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Ready for a <span className="gradient-text">free quote?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Phone, email or drop in — Unit 2, 1 Doric Way, Islington, Christchurch.
            </p>
            <Button variant="hero" size="xl" asChild>
              <Link to="/contact">Get in touch</Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
